import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, Button, CircularProgress, Container } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useAuth } from '../contexts/AuthContext';

// Must match GetStarted.tsx's PENDING_PLAN_KEY — a mid-onboarding user who
// left to click this link should land back in the wizard (resuming at
// Verify), not at a dead-end confirmation screen or the dashboard.
const PENDING_PLAN_KEY = 'dmo_pending_onboarding_plan';

const ConfirmEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  const continueTarget = () => {
    if (!auth.isSignedIn) return '/login';
    try {
      const raw = localStorage.getItem(PENDING_PLAN_KEY);
      if (raw) {
        const pending = JSON.parse(raw) as { userId?: string };
        if (pending.userId === auth.user?.id) return '/get-started';
      }
    } catch {
      // malformed entry — fall through to the dashboard
    }
    return '/';
  };

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage('This confirmation link is missing its token.');
      return;
    }

    fetch(`/api/auth/confirm-email?token=${encodeURIComponent(token)}`)
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.error || 'This confirmation link is invalid or has expired.');
        }
        setStatus('success');
      })
      .catch((err) => {
        setStatus('error');
        setMessage(err instanceof Error ? err.message : 'This confirmation link is invalid or has expired.');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container maxWidth="sm">
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <Paper sx={{ p: 4, textAlign: 'center', width: '100%' }}>
          {status === 'loading' && <CircularProgress />}
          {status === 'success' && (
            <>
              <CheckCircleIcon color="success" sx={{ fontSize: 56, mb: 2 }} />
              <Typography variant="h6" gutterBottom>Email Confirmed</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Your email address has been confirmed.
              </Typography>
              <Button variant="contained" onClick={() => navigate(continueTarget())}>
                Continue
              </Button>
            </>
          )}
          {status === 'error' && (
            <>
              <ErrorOutlineIcon color="error" sx={{ fontSize: 56, mb: 2 }} />
              <Typography variant="h6" gutterBottom>Confirmation Failed</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {message}
              </Typography>
              <Button variant="contained" onClick={() => navigate(continueTarget())}>
                Continue
              </Button>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default ConfirmEmail;
